import { useState, useLayoutEffect } from "react"
import { Row, Col } from "react-bootstrap"
import FilterReport from "../header/FilterReport"
import styles from "../style.module.css"
import { format, startOfMonth } from "date-fns"
import axiosService from "../../../utils/axios.service"
import { Table, Spin } from "antd"
import "../scss/report.scss"
import StorageService from "../../../utils/storage.service"

const emptySummary = {
    new_card_customers: 0, new_card_amount: 0,
    debt_customers: 0, debt_amount: 0,
    new_customers: 0, new_amount: 0,
    course_customers: 0, course_amount: 0,
    total_amount: 0,
}

const money = (value) => new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(value || 0)
const percent = (value) => `${((value || 0) * 100).toFixed(1)}%`
const groupLabel = {
    new_card: "Khách mua thẻ mới",
    debt: "Khách hàng trả nợ",
    new: "Khách hàng mới",
    course: "Khách hàng liệu trình",
}

const ReportExpectedCash = () => {
    const today = format(new Date(), "yyyy-MM-dd")
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"))
    const [endDate, setEndDate] = useState(today)
    const [store, setStore] = useState(0)
    const [rows, setRows] = useState([])
    const [details, setDetails] = useState([])
    const [summary, setSummary] = useState(emptySummary)
    const [rates, setRates] = useState({ new: {}, course: {} })
    const [loading, setLoading] = useState(false)
    const [userAdmin] = useState(JSON.parse(StorageService.getItem("user_info")))

    const fetchData = async () => {
        setLoading(true)
        const res = await axiosService("api/reports/expected-cash", "POST", { store, startDate, endDate })
        if (res?.data?.code === 200) {
            setSummary(res.data.data?.summary || emptySummary)
            setRows((res.data.data?.stores || []).map((item) => ({ ...item, key: item.store_id })))
            setDetails((res.data.data?.details || []).map((item) => ({ ...item, key: item.booking_id })))
            setRates(res.data.data?.rates || { new: {}, course: {} })
        } else {
            setSummary(emptySummary)
            setRows([])
            setDetails([])
        }
        setLoading(false)
    }

    useLayoutEffect(() => {
        fetchData()
    }, [startDate, endDate, store])

    const storeColumns = [
        { title: "Cơ sở", dataIndex: "store_name", fixed: "left", width: 180, ellipsis: true },
        { title: "KH mua thẻ mới", dataIndex: "new_card_customers", align: "center", width: 120 },
        { title: "Tiền thẻ mới", dataIndex: "new_card_amount", align: "right", width: 140, render: money },
        { title: "KH trả nợ", dataIndex: "debt_customers", align: "center", width: 110 },
        { title: "Tiền trả nợ", dataIndex: "debt_amount", align: "right", width: 140, render: money },
        { title: "KH mới", dataIndex: "new_customers", align: "center", width: 100 },
        { title: "Dự kiến KH mới", dataIndex: "new_amount", align: "right", width: 150, render: money },
        { title: "KH liệu trình", dataIndex: "course_customers", align: "center", width: 120 },
        { title: "Dự kiến liệu trình", dataIndex: "course_amount", align: "right", width: 160, render: money },
        { title: "Tổng dự kiến", dataIndex: "total_amount", align: "right", width: 150, render: money },
    ]
    const detailColumns = [
        { title: "Khách hàng", dataIndex: "full_name", fixed: "left", width: 160, ellipsis: true },
        { title: "SĐT", dataIndex: "mobile", width: 120 },
        { title: "Cơ sở", dataIndex: "store_name", width: 160, ellipsis: true },
        { title: "Ngày booking", dataIndex: "book_date", width: 160, render: (v) => v ? format(new Date(v), "dd-MM-yyyy HH:mm") : "" },
        { title: "Nhóm", dataIndex: "group", width: 170, render: (v) => groupLabel[v] || v },
        { title: "Tiền dự kiến", dataIndex: "amount", align: "right", width: 140, render: money },
    ]

    const metrics = [
        ["Tổng tiền thu dự kiến", money(summary.total_amount)],
        ["KH mua thẻ mới", `${summary.new_card_customers} / ${money(summary.new_card_amount)}`],
        ["KH trả nợ", `${summary.debt_customers} / ${money(summary.debt_amount)}`],
        ["KH mới (dự kiến)", `${summary.new_customers} / ${money(summary.new_amount)}`],
        ["KH liệu trình (dự kiến)", `${summary.course_customers} / ${money(summary.course_amount)}`],
        ["Tỷ lệ chốt Upsale mới", percent(rates?.new?.close_rate)],
        ["AOV Upsale mới", money(rates?.new?.aov_buyer)],
        ["Tỷ lệ chốt Upsale liệu trình", percent(rates?.course?.close_rate)],
        ["AOV Upsale liệu trình", money(rates?.course?.aov_buyer)],
    ]

    return (
        <div className={`${styles.background} container-fluid report-page course-customer-report`}>
            <FilterReport
                startDate={startDate}
                endDate={endDate}
                handleRangeDate={(sDate, eDate) => {
                    setStartDate(sDate)
                    setEndDate(eDate)
                }}
                store={store}
                handleStore={setStore}
                title="Báo cáo Tiền thu dự kiến"
                type="expected-cash"
                userAdmin={userAdmin}
            />
            <Spin spinning={loading}>
                <Row className="mb-3">
                    {metrics.map(([label, value], index) => (
                        <Col key={label} xs={6} md={4} lg={2} className="mb-2">
                            <div className={`course-kpi${index === 0 ? " is-primary" : ""}`}>
                                <div className="course-kpi-label">{label}</div>
                                <strong>{value}</strong>
                            </div>
                        </Col>
                    ))}
                </Row>
                <div className="course-panel">
                    <Table
                        className="course-grid"
                        columns={storeColumns}
                        dataSource={rows}
                        pagination={false}
                        bordered
                        tableLayout="fixed"
                        scroll={{ x: 1600 }}
                        locale={{ emptyText: "Không có dữ liệu trong bộ lọc này" }}
                    />
                </div>
                <div className="bg-white mt-3 card-service-matrix">
                    <div style={{ padding: "14px 16px 8px", fontSize: 16, fontWeight: 700 }}>
                        Chi tiết theo booking
                    </div>
                    <div className="px-2 pb-2">
                        <Table
                            columns={detailColumns}
                            dataSource={details}
                            size="small"
                            bordered
                            scroll={{ x: 1100, y: 480 }}
                            pagination={{ pageSize: 20, showSizeChanger: false }}
                            locale={{ emptyText: "Không có booking trong bộ lọc này" }}
                        />
                    </div>
                </div>
            </Spin>
        </div>
    )
}

export default ReportExpectedCash
